// built-from: /engagement-attention-overview v1 impl:engagement-attention-overview role:implementation
import { Link } from 'react-router-dom';
import type { LumenGridModernisationJourney } from '../fixtures/modernisation/scenarios';
import type { DiscoveryPlan, DurableDiscoveryActivity } from '../hooks/useDiscoveryPlan';
import type { Engagement } from '../hooks/useEngagement';

type Props = {
  journey: LumenGridModernisationJourney;
  activityPath: string;
  engagement?: Engagement | null;
  discoveryPlan?: DiscoveryPlan | null;
  loading?: boolean;
  startMappingPath?: string;
};

function ownerName(activity: DurableDiscoveryActivity): string {
  if (typeof activity.owner === 'string') return activity.owner;
  return activity.owner?.name || 'Owner to confirm';
}

function directionOf(engagement: Engagement): { headline: string | null; goalCount: number } {
  const vision = engagement.vision as {
    goals?: unknown[];
    coverStory?: { headline?: unknown } | null;
  } | null;
  const headline = typeof vision?.coverStory?.headline === 'string'
    && vision.coverStory.headline.trim()
    ? vision.coverStory.headline.trim()
    : null;
  return {
    headline,
    goalCount: Array.isArray(vision?.goals) ? vision.goals.length : 0,
  };
}

function RealEngagementOverview({
  engagement,
  discoveryPlan,
  loading = false,
  startMappingPath = '/engagement/discovery/current-state-mapping/start',
}: { engagement: Engagement } & Pick<Props, 'discoveryPlan' | 'loading' | 'startMappingPath'>): JSX.Element {
  const activities = discoveryPlan?.activities ?? [];
  const proposedDecisions = discoveryPlan?.scopeDecisions.filter(
    (decision) => decision.state === 'proposed',
  ) ?? [];
  const activeActivities = activities.filter((activity) => activity.state !== 'complete');
  const attentionCount = activeActivities.length + proposedDecisions.length;
  const direction = directionOf(engagement);

  return (
    <div className="journey-page">
      <header className="journey-page__header">
        <span className="journey-eyebrow">Overview</span>
        <h1>Engagement overview</h1>
        <p>{engagement.client} · {engagement.programme}</p>
      </header>

      <section className="journey-panel journey-attention" aria-labelledby="attention-title">
        <div className="journey-section-heading">
          <div>
            <span className="journey-eyebrow">Your work</span>
            <h2 id="attention-title">Needs your attention</h2>
            <p>These are the activities and decisions currently waiting for you.</p>
          </div>
          <span className="journey-count">{attentionCount} {attentionCount === 1 ? 'item' : 'items'}</span>
        </div>

        {loading ? (
          <div className="journey-empty-state" role="status">Loading engagement work…</div>
        ) : activities.length === 0 ? (
          <div className="journey-empty-state" role="status">
            <strong>No Discovery activities yet</strong>
            <span>Start with the work needed now; add other activities as evidence shows they are needed.</span>
            <Link className="journey-button journey-button--primary" to={startMappingPath}>
              Start current-state mapping
            </Link>
          </div>
        ) : attentionCount === 0 ? (
          <div className="journey-empty-state" role="status">
            <strong>Nothing needs your attention.</strong>
            <span>Completed Discovery work remains available from the phase workspace.</span>
            <Link className="journey-button journey-button--secondary" to="/engagement/discovery">
              Open Discovery
            </Link>
          </div>
        ) : (
          <div className="journey-attention-list">
            {activeActivities.map((activity, index) => {
              const path = activity.definition.id === 'map-current-value-streams'
                ? `/engagement/discovery/activities/${encodeURIComponent(activity.id)}`
                : '/engagement/discovery';
              return (
                <Link
                  className={`journey-attention-row${index === 0 ? ' journey-attention-row--next' : ''}`}
                  key={activity.id}
                  to={path}
                >
                  <span className="journey-attention-row__marker" aria-hidden="true">{index + 1}</span>
                  <span className="journey-attention-row__body">
                    <span className="journey-kicker">
                      {activity.state === 'awaiting-review' ? 'Output review' : 'Discovery activity'}
                    </span>
                    <strong>{activity.definition.name}</strong>
                    <small>{activity.definition.method} · Owner: {ownerName(activity)}</small>
                  </span>
                  <span className="journey-row-action">
                    {activity.state === 'awaiting-review' ? 'Review output' : 'Open activity'}
                  </span>
                </Link>
              );
            })}
            {proposedDecisions.map((decision, index) => (
              <Link
                className="journey-attention-row"
                key={decision.id}
                to="/engagement/discovery#scope-decisions"
              >
                <span className="journey-attention-row__marker" aria-hidden="true">
                  {activeActivities.length + index + 1}
                </span>
                <span className="journey-attention-row__body">
                  <span className="journey-kicker">Scope decision</span>
                  <strong>Review proposed Discovery work</strong>
                  <small>A proposed activity is waiting for a scope decision.</small>
                </span>
                <span className="journey-row-action">Review scope</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="journey-support-grid">
        <section className="journey-panel" aria-labelledby="direction-summary-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Engagement direction</span>
              <h2 id="direction-summary-title">{direction.headline || 'Direction not yet captured'}</h2>
            </div>
            <Link className="journey-text-link" to="/engagement/direction">View direction</Link>
          </div>
          <p className="journey-summary-copy">
            {direction.headline
              ? 'The confirmed direction for this engagement.'
              : 'No agreed direction has been recorded for this engagement yet.'}
          </p>
          <div className="journey-fact-row">
            <span><strong>{direction.goalCount}</strong> captured goals</span>
          </div>
        </section>

        <section className="journey-panel" aria-labelledby="phase-summary-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Current phase</span>
              <h2 id="phase-summary-title">Discovery</h2>
            </div>
            <Link className="journey-text-link" to="/engagement/discovery">Open Discovery</Link>
          </div>
          <p className="journey-summary-copy">Build enough evidence to decide what should change, why, and where to start.</p>
          <div className="journey-fact-row">
            <span><strong>{activities.length}</strong> activities in scope</span>
            <span><strong>{proposedDecisions.length}</strong> scope decisions</span>
          </div>
        </section>
      </div>
    </div>
  );
}

export function EngagementAttentionOverview({
  journey,
  activityPath,
  engagement,
  discoveryPlan,
  loading,
  startMappingPath,
}: Props): JSX.Element {
  if (engagement) {
    return (
      <RealEngagementOverview
        engagement={engagement}
        discoveryPlan={discoveryPlan}
        loading={loading}
        startMappingPath={startMappingPath}
      />
    );
  }

  const proposed = journey.activities.find((activity) => activity.state === 'proposed');
  const awaitingReview = journey.activities.find((activity) => activity.state === 'awaiting-review');
  const inScope = journey.activities.filter((activity) => activity.state !== 'proposed');

  return (
    <div className="journey-page">
      <header className="journey-page__header">
        <span className="journey-eyebrow">Overview</span>
        <h1>Good morning, {journey.consultant.firstName}</h1>
        <p>{journey.client.name} · {journey.programme}</p>
      </header>

      <section className="journey-panel journey-attention" aria-labelledby="attention-title">
        <div className="journey-section-heading">
          <div>
            <span className="journey-eyebrow">Your work</span>
            <h2 id="attention-title">Needs your attention</h2>
            <p>These are the activities and decisions currently waiting for you.</p>
          </div>
          <span className="journey-count">3 items</span>
        </div>

        <div className="journey-attention-list">
          <Link className="journey-attention-row journey-attention-row--next" to={activityPath}>
            <span className="journey-attention-row__marker" aria-hidden="true">1</span>
            <span className="journey-attention-row__body">
              <span className="journey-kicker">Next activity</span>
              <strong>Map priority value streams</strong>
              <small>Workshop participants are confirmed · Owner: {journey.consultant.name}</small>
            </span>
            <span className="journey-row-action">Open activity</span>
          </Link>

          {awaitingReview ? (
            <Link className="journey-attention-row" to="/engagement/discovery">
              <span className="journey-attention-row__marker" aria-hidden="true">2</span>
              <span className="journey-attention-row__body">
                <span className="journey-kicker">Output review</span>
                <strong>{awaitingReview.output.name}</strong>
                <small>{awaitingReview.definition.name} · Submitted for review</small>
              </span>
              <span className="journey-row-action">Open Discovery</span>
            </Link>
          ) : null}

          {proposed ? (
            <Link className="journey-attention-row" to="/engagement/discovery#scope-decisions">
              <span className="journey-attention-row__marker" aria-hidden="true">3</span>
              <span className="journey-attention-row__body">
                <span className="journey-kicker">Scope decision</span>
                <strong>{proposed.definition.name}</strong>
                <small>Suggested from supplied evidence · Not yet in scope</small>
              </span>
              <span className="journey-row-action">Review scope</span>
            </Link>
          ) : null}
        </div>
      </section>

      <div className="journey-support-grid">
        <section className="journey-panel" aria-labelledby="direction-summary-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Engagement direction</span>
              <h2 id="direction-summary-title">{journey.goal.name}</h2>
            </div>
            <Link className="journey-text-link" to="/engagement/direction">View direction</Link>
          </div>
          <p className="journey-summary-copy">{journey.goal.statement}</p>
          <div className="journey-fact-row">
            <span><strong>{journey.outcomes.length}</strong> agreed outcomes</span>
            <span><strong>{journey.measures.length}</strong> measures</span>
          </div>
        </section>

        <section className="journey-panel" aria-labelledby="phase-summary-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Current phase</span>
              <h2 id="phase-summary-title">Discovery</h2>
            </div>
            <Link className="journey-text-link" to="/engagement/discovery">Open Discovery</Link>
          </div>
          <p className="journey-summary-copy">Build enough evidence to decide what should change, why, and where to start.</p>
          <div className="journey-fact-row">
            <span><strong>{inScope.length}</strong> activities in scope</span>
            <span><strong>1</strong> scope decision</span>
          </div>
        </section>
      </div>
    </div>
  );
}
